"use client";

/**
 * react-router-dom compatibility layer built on top of next/navigation.
 *
 * The app was migrated from a Vite SPA that used react-router. Rather than
 * rewriting every call site, this module re-implements the three hooks the app
 * actually used with identical signatures, so component code stays untouched.
 */

import { useCallback, useMemo } from "react";
import {
  usePathname,
  useRouter,
  useSearchParams as useNextSearchParams,
} from "next/navigation";

export type NavigateOptions = {
  replace?: boolean;
  /** Accepted for API parity with react-router; Next has no history state. */
  state?: unknown;
};

export type NavigateFunction = {
  (to: string, options?: NavigateOptions): void;
  /** `navigate(-1)` goes back, `navigate(1)` goes forward. */
  (delta: number): void;
};

/** Drop-in replacement for react-router's `useNavigate`. */
export function useNavigate(): NavigateFunction {
  const router = useRouter();

  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        if (to < 0) router.back();
        else if (to > 0) router.forward();
        return;
      }
      if (options?.replace) router.replace(to);
      else router.push(to);
    },
    [router]
  ) as NavigateFunction;
}

export type Location = {
  pathname: string;
  /** Leading "?" included when non-empty, matching react-router. */
  search: string;
  hash: string;
  state: unknown;
  key: string;
};

/** Drop-in replacement for react-router's `useLocation`. */
export function useLocation(): Location {
  const pathname = usePathname();
  const params = useNextSearchParams();
  const query = params?.toString() ?? "";

  return useMemo(
    () => ({
      pathname: pathname ?? "/",
      search: query ? `?${query}` : "",
      hash: typeof window !== "undefined" ? window.location.hash : "",
      state: null,
      key: "default",
    }),
    [pathname, query]
  );
}

type SearchParamsInit =
  | URLSearchParams
  | Record<string, string | string[]>
  | string
  | string[][];

export type SetSearchParams = (
  next: SearchParamsInit | ((prev: URLSearchParams) => SearchParamsInit),
  options?: { replace?: boolean }
) => void;

function toURLSearchParams(init: SearchParamsInit): URLSearchParams {
  if (init instanceof URLSearchParams) return new URLSearchParams(init.toString());
  if (typeof init === "string" || Array.isArray(init)) return new URLSearchParams(init as never);

  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(init)) {
    if (Array.isArray(value)) value.forEach((v) => out.append(key, v));
    else if (value != null) out.set(key, value);
  }
  return out;
}

/**
 * Drop-in replacement for react-router's `useSearchParams`.
 *
 * Returns a *mutable* copy of the current params, because call sites mutate it
 * in place (`params.delete(...)`) before handing it back to the setter — which
 * would throw on Next's read-only `ReadonlyURLSearchParams`.
 */
export function useSearchParams(): [URLSearchParams, SetSearchParams] {
  const router = useRouter();
  const pathname = usePathname();
  const readonlyParams = useNextSearchParams();
  const serialized = readonlyParams?.toString() ?? "";

  const params = useMemo(() => new URLSearchParams(serialized), [serialized]);

  const setSearchParams = useCallback<SetSearchParams>(
    (next, options) => {
      const resolved =
        typeof next === "function" ? next(new URLSearchParams(serialized)) : next;
      const query = toURLSearchParams(resolved).toString();
      const url = query ? `${pathname}?${query}` : pathname;

      if (options?.replace) router.replace(url, { scroll: false });
      else router.push(url, { scroll: false });
    },
    [router, pathname, serialized]
  );

  return [params, setSearchParams];
}

/** Reads the public env var that older code read via `import.meta.env`. */
export const LOGOUT_REDIRECT = process.env.NEXT_PUBLIC_LOGOUT_REDIRECT ?? "/auth";
